/**
 * This component handles all the permissions assigned to a specific role.
 */

import React, { useEffect, useState, useRef, useImperativeHandle } from "react";
import { useNavigate } from "react-router-dom";
import { connect, useDispatch } from "react-redux";

//ANT DESIGN IMPORT
import {
  Card,
  List,
  Row,
  Col,
  Tooltip,
  Button,
  Tag,
  Badge,
  Divider,
  Spin,
  Table,
  Switch,
  Descriptions,
} from "antd";
import {
  LockOutlined,
  PlusCircleFilled,
  CheckOutlined,
  CloseOutlined,
  GroupOutlined,
  SaveOutlined,
} from "@ant-design/icons";

//PROJECT IMPORTS
import styles from "../../../../assets/styles/styles";
import { apiConfig, labelConfig, staticConfig } from "../../security.config";
import { BOM } from "../../../../services/definedMethod";
import cnt from "../../../../services/definedConstant";
import CustomButton from "../../../../components/custom/CustomButton";
import { elements } from "chart.js";
import { showCheckerApprovalModal } from "../../../../components/CheckerModal";

const AssignedPermissions = (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { userData, roleId, state } = props;
  const { session } = userData;
  const [isLoading, setIsLoading] = useState(false);

  const [permissionsData, setPermissionsData] = useState([]);
  const [rowPermissionsData, setRowPermissionsData] = useState([]);
  const [permissionCodes, setPermissionCodes] = useState({});
  const [permissionsObj, setPermissionsObj] = useState({});

  const columns = [
    {
      title: "Permissions Group Name",
      dataIndex: "displayGroupName",
    },
    Table.EXPAND_COLUMN,
    {
      title: "Total Permissions",
      dataIndex: "groupName",
      render: (record) => {
        const groupCount = rowPermissionsData.filter(
          (item) => item.grouping === record
        );
        let color = groupCount.length < 10 ? "geekblue" : "green";
        return <Tag color={color}>{groupCount.length}</Tag>;
      },
    },
  ];

  useEffect(() => {
    getDetailById();
  }, []);

  const getDetailById = () => {
    const url = `${apiConfig.Roles}/${roleId}/permissions`;

    let body = {
      session,
    };

    setIsLoading(true);
    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err.defaultUserMessage);
      } else {
        const objList = res.permissionUsageData;
        const tempPermission = [];
        const tempPermissionCodes = {};
        setPermissionsObj(res);

        objList.forEach((element, index) => {
          tempPermission.push({
            key: index + 1,
            displayGroupName: formatName(element.grouping),
            groupName: element.grouping,
          });

          //   creation of one permission and
          tempPermissionCodes[objList[index].code] = objList[index].selected;
        });

        // remove duplicate
        setPermissionsData(BOM.GetUnique(tempPermission, "groupName"));

        setRowPermissionsData(objList);

        //this will help us when saving
        setPermissionCodes(tempPermissionCodes);

        {
          BOM.HasPermission("DELETE_ROLE") &&
            dispatch({ type: cnt.RIGHT_DELETE_BTN });
        }

        switch (res.disabled) {
          case true:
            {
              BOM.HasPermission("ENABLE_ROLE") &&
                dispatch({ type: cnt.RIGHT_APPROVE_BTN });
            }
            break;
          case false:
            {
              BOM.HasPermission("DISABLE_ROLE") &&
                dispatch({ type: cnt.RIGHT_REJECT_BTN });
            }
            break;
          default:
            break;
        }
        setIsLoading(false);
      }
    });
  };

  const items = [
    {
      label: labelConfig.name,
      children: permissionsObj ? permissionsObj.name : "-",
    },
    {
      label: labelConfig.description,
      children: permissionsObj ? permissionsObj.description : "-",
    },
  ];

  const formatName = (string) => {
    if (string.indexOf("portfolio_") > -1) {
      string = string.replace("portfolio_", "");
    }
    if (string.indexOf("transaction_") > -1) {
      var temp = string.split("_");
      string =
        temp[1] +
        " " +
        temp[0].charAt(0).toUpperCase() +
        temp[0].slice(1) +
        "s";
    }
    string = string.charAt(0).toUpperCase() + string.slice(1);
    return string;
  };

  function onCancel() {
    navigate("/sms0400", { state: state });
  }

  const onSubmit = () => {
    const url = `${apiConfig.Roles}/${roleId}/permissions`;

    let body = {
      session,
      method: "PUT",
      permissions: permissionCodes,
    };

    setIsLoading(true);

    BOM.FetchReqAction(
      body,
      url,
      (err, res) => {
        if (err) {
          BOM.AlertMsg(cnt.DANGER, err.defaultUserMessage);
        } else {
          // getDetailById();
          BOM.AlertMsg(
            cnt.SUCCESS,
            "Role permissions were successfully updated."
          );
          navigate("/sms0400");

          setIsLoading(false);
        }
      },
      {
        navigate,
        showCheckerModal: showCheckerApprovalModal,
        onCheckerApproval: (res) => {
          navigate("/sms0400");
          setIsLoading(false);
        },
      }
    );
  };

  return (
    <Card
      title={
        <div style={{ marginTop: 5 }}>
          <Badge color="var(--of-purple)" count={permissionsData.length}>
            <span style={{ margin: 12 }}>List of Group Permissions</span>
          </Badge>
        </div>
      }
      style={{
        boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
        borderRadius: 20,
        minHeight: 300,
        maxHeight: 540,
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <Spin spinning={permissionsData.length === 0} size="large">
        <Row gutter={[24, 0]}>
          <Descriptions
            bordered
            column={{
              xs: 1,
              sm: 1,
              md: 1,
              lg: 1,
              xl: 1,
              xxl: 1,
            }}
            style={{ marginTop: 10, marginBottom: 20 }}
            items={items}
          />
          <Col xs={24} lg={24}>
            <Table
              columns={columns}
              expandable={{
                expandedRowRender: (record) => {
                  let tempGroup = rowPermissionsData.filter(
                    (item) => item.grouping === record.groupName
                  );

                  const onSelectAll = (objParam) => {
                    rowPermissionsData
                      .filter((item) => item.grouping === objParam.groupName)
                      .forEach((element, index) => {
                        setPermissionCodes((prevState) => ({
                          ...prevState,
                          [element.code]: true,
                        }));
                      });

                    tempGroup.forEach((element) => {
                      element.selected = true;
                    });
                  };

                  const onDeselectAll = (objParam) => {
                    rowPermissionsData
                      .filter((item) => item.grouping === objParam.groupName)
                      .forEach((element, index) => {
                        setPermissionCodes((prevState) => ({
                          ...prevState,
                          [element.code]: false,
                        }));
                      });

                    tempGroup.forEach((element) => {
                      element.selected = false;
                    });
                  };

                  const onSelectPermission = (event, objParam) => {
                    /**
                     * UPDATE SPECIFIC PERMISSION
                     * event: is the switch value
                     */
                    const selectedObjIndex = tempGroup.findIndex(
                      (element) => element.code === objParam.code
                    );

                    switch (event) {
                      case true:
                        tempGroup[selectedObjIndex].selected = true;
                        setPermissionCodes({
                          ...permissionCodes,
                          [objParam.code]: true,
                        });
                        break;

                      default:
                        tempGroup[selectedObjIndex].selected = false;
                        setPermissionCodes({
                          ...permissionCodes,
                          [objParam.code]: false,
                        });
                        break;
                    }
                  };

                  return (
                    <>
                      <Divider orientation="right">
                        <Tooltip
                          placement="top"
                          title={`Select all permissions for ${record.groupName}`}
                        >
                          <Button
                            size="small"
                            shape="round"
                            className="of-primaryBtnColor"
                            onClick={() => onSelectAll(record)}
                            disabled={!BOM.HasPermission("UPDATE_ROLE")}
                          >
                            &nbsp; Select All
                          </Button>
                        </Tooltip>

                        <Tooltip
                          placement="top"
                          title={`Deselect all permissions for ${record.groupName}`}
                        >
                          <Button
                            size="small"
                            shape="round"
                            className="of-primaryBtnColor"
                            onClick={() => onDeselectAll(record)}
                            style={{ marginLeft: 10 }}
                            disabled={!BOM.HasPermission("UPDATE_ROLE")}
                          >
                            &nbsp; Deselect All
                          </Button>
                        </Tooltip>
                      </Divider>

                      <List
                        bordered
                        pagination={{ pageSize: 10, align: "center" }}
                        dataSource={tempGroup}
                        renderItem={(item, index) => (
                          <List.Item
                            key={index}
                            actions={[
                              <Switch
                                style={{ fontSize: 12 }}
                                checkedChildren={<CheckOutlined />}
                                unCheckedChildren={<CloseOutlined />}
                                checked={item.selected}
                                onClick={(e) => onSelectPermission(e, item)}
                                disabled={!BOM.HasPermission("UPDATE_ROLE")}
                              />,
                            ]}
                          >
                            <List.Item.Meta
                              avatar={
                                <LockOutlined style={styles.iconSize12} />
                              }
                              title={
                                <div style={{ fontSize: 12 }}>
                                  &nbsp; {item.code}
                                </div>
                              }
                              description={
                                <div style={{ fontSize: 12 }}>
                                  {item.grouping} | {item.entityName}
                                </div>
                              }
                            />
                          </List.Item>
                        )}
                      />
                    </>
                  );
                },
              }}
              dataSource={permissionsData}
            />
          </Col>
        </Row>

        <Row>
          <Divider />
          {BOM.HasPermission("UPDATE_ROLE") && (
            <CustomButton
              content="Submit"
              style={{
                width: "10%",
                marginLeft: "auto",
                boxShadow: "0px 0px 10px 5px rgba(0,0,0,0.1)",
              }}
              htmlType="submit"
              backgroundColor="#92428B"
              colorText="white"
              hoverBackground="#AD4EA7"
              onClick={() => onSubmit()}
            />
          )}

          {BOM.HasPermission("UPDATE_ROLE") && (
            <CustomButton
              content="Cancel"
              style={{
                width: "10%",
                marginLeft: "1%",
                boxShadow: "0px 0px 10px 5px rgba(0,0,0,0.1)",
              }}
              backgroundColor="#fff"
              colorText="black"
              hoverBackground="#F5F5F5"
              onClick={onCancel}
            />
          )}
        </Row>
      </Spin>
    </Card>
  );
};

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
  rightBtn: state.RightSideBtn,
});
const mapDispatchToProps = {};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AssignedPermissions);
